import {differenceInDays, differenceInMinutes, intervalToDuration, parseISO} from 'date-fns';
import {formatDuration} from '../meetings';
import {roundDownDurationIfMoreThanDay, stripSecondsAndMilliseconds} from '@lib/utils';
import {Area} from '../entities';
import {WorkspaceOccupancyState} from '@lib/infrastructure';
import {SensorIcon, iconForSensorData} from '@map/logic';
import {TranslationService} from '../../@types/i18next';

export type AreaSensorInfo = {
  title: string;
  status: SensorIcon;
};

export const getSensorLastUpdatedText = (t: TranslationService, sensorStateTimestamp?: string) => {
  if (!sensorStateTimestamp) return;

  const parsedTimestamp = stripSecondsAndMilliseconds(parseISO(sensorStateTimestamp));
  const now = stripSecondsAndMilliseconds(new Date());

  const sensorLastUpdatedAgo = intervalToDuration({start: parsedTimestamp, end: now});
  const roundedDuration = roundDownDurationIfMoreThanDay(sensorLastUpdatedAgo);
  const isUpdatedLessThanMinuteAgo = differenceInMinutes(now, parsedTimestamp) < 1;
  const isUpdatedMoreThanDayAgo = differenceInDays(now, parsedTimestamp) > 0;

  const formattedDuration = formatDuration(t, roundedDuration, isUpdatedMoreThanDayAgo);
  return isUpdatedLessThanMinuteAgo
    ? t('hereAndNow:RoomSensorAvailabilityUpdatedJustNow')
    : t('hereAndNow:RoomSensorAvailabilityUpdateTime', {
        countWithMinutesSuffix: formattedDuration,
      });
};

export const getAreaSensorInfo = (
  t: TranslationService,
  area?: Area,
  sensorState?: WorkspaceOccupancyState | null,
): AreaSensorInfo | null => {
  const iconName =
    sensorState && area
      ? iconForSensorData({
          ...sensorState.workplaceState,
          capacity: area.capacity || area.adHocCapacity,
        })
      : null;

  switch (iconName) {
    case 'full':
      return {
        title: t('ItSeemsFull'),
        status: iconName,
      };
    case 'busy':
      return {
        title: t('ItSeemsBusy'),
        status: iconName,
      };
    case 'lively':
      return {
        title: t('ItSeemsLively'),
        status: iconName,
      };
    case 'quiet':
      return {
        title: t('ItSeemsQuiet'),
        status: iconName,
      };
    case 'unknown':
      return {
        title: t('NoSensorData'),
        status: iconName,
      };
    default:
      return null;
  }
};
